import { Loader2, X } from 'lucide-react'
import {
  DEFAULT_MODEL_STACK,
  MODEL_STACK_OPTIONS,
  type ModelStackId,
} from '../../../lib/optimization/modelStacks'
import { SUPPORT_EMAIL, supportMailtoHref } from '../../../lib/supportContact'
import { useDialogKeyboardShortcuts } from '../../../hooks/useDialogKeyboardShortcuts'

const MAX_JD_LENGTH = 20_000

interface JobDescriptionModalProps {
  isOpen: boolean
  value: string
  modelStack: ModelStackId
  eligibilityWarnings?: Array<{ code: string; title: string; detail: string }>
  eligibilityLoading?: boolean
  eligibilityReady?: boolean
  jdPrecheckLoading?: boolean
  jdSecurityBlocked?: boolean
  jdSecurityMessage?: string | null
  jdSecurityMediumMessage?: string | null
  jdPrecheckNetworkWarning?: string | null
  onChange: (value: string) => void
  onModelStackChange: (stack: ModelStackId) => void
  onSubmit: () => void
  onCancel: () => void
  submitting: boolean
  error: string | null
}

const MIN_JD_LENGTH = 80

export default function JobDescriptionModal({
  isOpen,
  value,
  modelStack,
  eligibilityWarnings = [],
  eligibilityLoading = false,
  eligibilityReady = false,
  jdPrecheckLoading = false,
  jdSecurityBlocked = false,
  jdSecurityMessage = null,
  jdSecurityMediumMessage = null,
  jdPrecheckNetworkWarning = null,
  onChange,
  onModelStackChange,
  onSubmit,
  onCancel,
  submitting,
  error,
}: JobDescriptionModalProps) {
  const trimmedLength = value.trim().length
  const hasEnoughText = trimmedLength >= MIN_JD_LENGTH
  const checksLoading = eligibilityLoading || jdPrecheckLoading
  const showStartButton =
    hasEnoughText && eligibilityReady && !checksLoading && !jdSecurityBlocked
  const canSubmit = showStartButton && !submitting

  useDialogKeyboardShortcuts({
    isOpen,
    onCancel,
    onConfirm: onSubmit,
    cancelDisabled: submitting,
    confirmDisabled: !canSubmit,
  })

  if (!isOpen) return null

  const handleClose = () => {
    if (!submitting) onCancel()
  }

  return (
    <div className="fixed inset-0 z-[55] overflow-y-auto">
      <div
        className="fixed inset-0 bg-overlay"
        onClick={handleClose}
        role="presentation"
        aria-hidden="true"
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="jd-modal-title"
          className="relative w-full max-w-2xl rounded-lg bg-surface-default shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-b border-border-default px-6 py-4 pr-14 relative">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="absolute right-4 top-4 rounded-md p-1 text-text-muted hover:bg-surface-muted hover:text-text-secondary disabled:opacity-50"
              aria-label="Close"
              data-testid="jd-modal-close"
            >
              <X className="h-5 w-5" />
            </button>
            <h2 id="jd-modal-title" className="text-lg font-semibold text-text-primary">
              Paste job description
            </h2>
            <p className="mt-1 text-sm text-text-secondary">
              Full Flow 2 needs the vacancy text before optimizing this resume. Title and company
              can be in the first lines.
            </p>
          </div>
          <div className="px-6 py-4 space-y-4">
            <div>
              <label
                htmlFor="model-stack-select"
                className="block text-sm font-medium text-text-secondary mb-1"
              >
                Model stack
              </label>
              <select
                id="model-stack-select"
                data-testid="optimization-model-stack"
                value={modelStack}
                onChange={(e) => onModelStackChange(e.target.value as ModelStackId)}
                disabled={submitting}
                className="w-full rounded-lg border border-border-default px-3 py-2 text-sm text-text-primary focus:border-interactive focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring/30"
              >
                {MODEL_STACK_OPTIONS.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-text-muted">
                {MODEL_STACK_OPTIONS.find((o) => o.id === modelStack)?.description}
              </p>
            </div>
            <div>
              <label htmlFor="jd-textarea" className="sr-only">
                Job description
              </label>
              <textarea
                id="jd-textarea"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                rows={14}
                maxLength={MAX_JD_LENGTH}
                className="w-full rounded-lg border border-border-default px-3 py-2 text-sm text-text-primary focus:border-interactive focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring/30"
                placeholder="Job title, company, and full description…"
                disabled={submitting}
              />
              <p className="mt-1 text-xs text-text-muted text-right">
                {trimmedLength.toLocaleString()} / {MAX_JD_LENGTH.toLocaleString()}
              </p>
            </div>
            {hasEnoughText && (
              <div className="min-h-[5.5rem] space-y-2" data-testid="eligibility-status">
                {(checksLoading) && (
                  <div
                    className="flex items-center gap-2 rounded-lg border border-border-default bg-surface-muted px-3 py-3"
                    data-testid="eligibility-loading"
                  >
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin text-text-muted" />
                    <p className="text-sm text-text-secondary">
                      {jdPrecheckLoading
                        ? 'Checking job description security…'
                        : 'Reviewing job description…'}
                    </p>
                  </div>
                )}
                {!checksLoading && jdSecurityBlocked && jdSecurityMessage && (
                  <div
                    className="rounded-lg border border-red-300 bg-red-50 px-3 py-2"
                    data-testid="jd-security-blocked"
                    role="alert"
                  >
                    <p className="text-sm text-red-900">{jdSecurityMessage}</p>
                    <p className="mt-2 text-sm text-red-800">
                      Contact{' '}
                      <a
                        href={supportMailtoHref('Job description blocked')}
                        className="font-medium underline"
                      >
                        {SUPPORT_EMAIL}
                      </a>
                    </p>
                  </div>
                )}
                {!checksLoading && !jdSecurityBlocked && jdSecurityMediumMessage && (
                  <div
                    className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2"
                    data-testid="jd-security-medium"
                  >
                    <p className="text-sm text-amber-950">{jdSecurityMediumMessage}</p>
                    <p className="mt-2 text-xs text-amber-900">
                      Questions?{' '}
                      <a href={supportMailtoHref('Job description security warning')} className="underline">
                        {SUPPORT_EMAIL}
                      </a>
                    </p>
                  </div>
                )}
                {!checksLoading && jdPrecheckNetworkWarning && (
                  <div
                    className="rounded-lg border border-border-default bg-surface-muted px-3 py-2"
                    data-testid="jd-precheck-network-warning"
                  >
                    <p className="text-sm text-text-secondary">{jdPrecheckNetworkWarning}</p>
                  </div>
                )}
                {!checksLoading && eligibilityReady && eligibilityWarnings.length > 0 && (
                  <div
                    className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 space-y-2"
                    data-testid="eligibility-warnings"
                  >
                    <p className="text-sm font-medium text-amber-950">Before you optimize</p>
                    <ul className="list-disc list-outside pl-4 space-y-1 text-sm text-amber-900">
                      {eligibilityWarnings.map((w) => (
                        <li key={w.code}>
                          <span className="font-medium">{w.title}</span>
                          {w.detail ? ` — ${w.detail}` : ''}
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-amber-800">
                      You can still start optimization if you want to proceed.
                    </p>
                  </div>
                )}
                {!checksLoading && eligibilityReady && eligibilityWarnings.length === 0 && !jdSecurityBlocked && (
                  <div
                    className="rounded-lg border border-border-default bg-surface-muted px-3 py-2"
                    data-testid="eligibility-clear"
                  >
                    <p className="text-sm text-text-secondary">
                      Review complete. You can start optimization.
                    </p>
                  </div>
                )}
              </div>
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
          {showStartButton && (
            <div className="flex justify-end border-t border-border-default px-6 py-4">
              <button
                type="button"
                data-testid="start-optimization-button"
                onClick={onSubmit}
                disabled={!canSubmit}
                className="rounded-lg bg-brand-primary px-4 py-2 text-sm font-medium text-white hover:bg-brand-primary-hover disabled:opacity-50"
              >
                {submitting ? 'Starting…' : 'Start optimization'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export { DEFAULT_MODEL_STACK }
